/**
 * HR → «Ставки» (26.08, владелец): ВСЯ матрица оператор×квест на одном экране.
 * Горизонтальная прокрутка, кнопка ⇄ меняет оси, шрифты мелкие. Пустая ставка
 * показывается как дефолт ₽350 (приглушённо). Тап по ячейке — NumPadPop, запись
 * через PUT /api/rates/operators (тот же канал, ручная правка главнее грейдов).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "../../api/client";
import type { ApiList } from "../../api/types";
import type { BoardOperator } from "../../hooks/useOoDay";
import { NumPadPop } from "../../components/shared";
import css from "./board.module.css";

const DEFAULT_RATE = 350;
interface QuestRow { id: string; name: string; short_name?: string; branch_id?: string; active?: number }
interface RawRate { operator_id: string; quest_id: string; rate: number }

export function RatesMatrix({ operators }: { operators: BoardOperator[] }) {
  const [quests, setQuests] = useState<QuestRow[]>([]);
  const [rates, setRates] = useState<Record<string, number>>({}); // `${opId}|${questId}`
  const [flip, setFlip] = useState(false);
  const [pick, setPick] = useState<{ opId: string; questId: string } | null>(null);
  const anchor = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let alive = true;
    api<ApiList<QuestRow>>("/api/quests").then((r) => { if (alive) setQuests((r.results ?? []).filter((q) => q.active !== 0)); }).catch(() => {});
    api<{ rates?: { results?: RawRate[] } }>("/api/rates/operators").then((r) => {
      if (!alive) return;
      setRates(Object.fromEntries((r.rates?.results ?? []).map((x) => [`${x.operator_id}|${x.quest_id}`, Number(x.rate) || 0])));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const qs = useMemo(() => [...quests].sort((a, b) => (a.branch_id ?? "").localeCompare(b.branch_id ?? "") || a.name.localeCompare(b.name, "ru")), [quests]);
  const ops = useMemo(() => [...operators].sort((a, b) => a.name.localeCompare(b.name, "ru")), [operators]);

  const save = (opId: string, questId: string, v: number) => {
    if (v <= 0) return;
    const key = `${opId}|${questId}`;
    const prev = rates[key];
    setRates((x) => ({ ...x, [key]: v }));
    api("/api/rates/operators", { method: "PUT", body: { operator_id: opId, rates: { [questId]: v } } })
      .catch(() => setRates((x) => { const n = { ...x }; if (prev == null) delete n[key]; else n[key] = prev; return n; }));
  };

  const cell = (opId: string, questId: string) => {
    const v = rates[`${opId}|${questId}`];
    return (
      <button key={`${opId}|${questId}`} type="button"
        className={`${css.rmCell} ${v == null ? css.rmDflt : ""}`}
        title="Тап — изменить ставку"
        onClick={(e) => { anchor.current = e.currentTarget; setPick({ opId, questId }); }}
      >{v ?? DEFAULT_RATE}</button>
    );
  };
  const cols = flip ? ops.length : qs.length;
  return (
    <div className={css.content}>
      <div className={css.rmScroll}>
        <div className={css.rmGrid} style={{ gridTemplateColumns: `64px repeat(${cols}, minmax(34px, 1fr))` }}>
          <button type="button" className={css.agFlip} title="Поменять оси" onClick={() => setFlip((f) => !f)}>⇄</button>
          {(flip ? ops.map((o) => o.name.split(" ")[0]) : qs.map((q) => q.short_name || q.name)).map((h, i) => (
            <div key={`h${i}`} className={css.rmHead}>{h}</div>
          ))}
          {!flip
            ? ops.map((o) => (
              <FragRow key={o.id}>
                <div className={css.rmName}>{o.name.split(" ")[0]}</div>
                {qs.map((q) => cell(o.id, q.id))}
              </FragRow>
            ))
            : qs.map((q) => (
              <FragRow key={q.id}>
                <div className={css.rmName}>{q.short_name || q.name}</div>
                {ops.map((o) => cell(o.id, q.id))}
              </FragRow>
            ))}
        </div>
      </div>
      <NumPadPop
        open={!!pick} anchor={anchor.current}
        label={`Ставка ₽ — ${pick ? `${ops.find((o) => o.id === pick.opId)?.name.split(" ")[0] ?? ""} · ${qs.find((q) => q.id === pick.questId)?.short_name ?? ""}` : ""}`}
        mode="money" value={pick ? rates[`${pick.opId}|${pick.questId}`] ?? DEFAULT_RATE : DEFAULT_RATE} max={99999}
        onCommit={(v) => { if (pick) save(pick.opId, pick.questId, v); }}
        onClose={() => setPick(null)}
      />
    </div>
  );
}

function FragRow({ children }: { children: ReactNode }) { return <>{children}</>; }
